import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { HardHat, PaperPlaneTilt, X, Minus } from "@phosphor-icons/react";

export function ChatWidget() {
  const { user } = useAuth();
  const [member, setMember] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const lastRef = useRef(null);
  const openRef = useRef(false);
  const endRef = useRef(null);

  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    let alive = true;
    api.get("/chat/members").then(() => { if (alive) setMember(true); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!member) return;
    const poll = async () => {
      try {
        const params = lastRef.current ? { after: lastRef.current } : {};
        const r = await api.get("/chat/messages", { params });
        if (r.data.length) {
          const initial = !lastRef.current;
          setMsgs((prev) => [...prev, ...r.data].slice(-100));
          lastRef.current = r.data[r.data.length - 1].created_at;
          if (!initial) {
            const incoming = r.data.filter((m) => (m.sender_id || m.user_id) !== user?.id);
            if (incoming.length && !openRef.current) {
              setUnread((u) => u + incoming.length);
              setOpen(true);
            }
          }
        }
      } catch (e) { /* silencioso */ }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [member, user?.id]);

  useEffect(() => { if (open) { setUnread(0); endRef.current?.scrollIntoView({ behavior: "smooth" }); } }, [open, msgs]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      const fd = new FormData(); fd.append("text", t);
      const r = await api.post("/chat/messages", fd);
      setMsgs((prev) => [...prev, r.data].slice(-100));
      lastRef.current = r.data.created_at;
    } catch (err) { toast.error("No se pudo enviar el mensaje"); }
  };

  if (!member) return null;

  return (
    <div className="fixed bottom-20 right-4 z-50" data-testid="chat-widget">
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-widget-open" title="Chat del equipo"
          className="relative w-14 h-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-orange-600 transition-colors duration-200">
          <HardHat size={26} weight="fill" />
          {unread > 0 && <span data-testid="chat-widget-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
        </button>
      )}
      {open && (
        <div className="w-80 sm:w-96 h-[28rem] bg-white rounded-lg shadow-2xl border border-border flex flex-col overflow-hidden" data-testid="chat-widget-panel">
          <div className="flex items-center justify-between px-3 py-2 bg-primary text-white">
            <div className="flex items-center gap-2 font-semibold text-sm"><HardHat size={18} weight="fill" /> Chat del equipo</div>
            <div className="flex items-center gap-1">
              <button onClick={() => setOpen(false)} data-testid="chat-widget-min" title="Minimizar" className="p-1 hover:bg-white/20 rounded transition-colors"><Minus size={16} weight="bold" /></button>
              <button onClick={() => setOpen(false)} data-testid="chat-widget-close" title="Cerrar" className="p-1 hover:bg-white/20 rounded transition-colors"><X size={16} weight="bold" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {msgs.length === 0 && <div className="text-xs text-muted-foreground text-center mt-4">Sin mensajes todavía. ¡Escribe el primero!</div>}
            {msgs.map((m, i) => {
              const own = (m.sender_id || m.user_id) === user?.id;
              return (
                <div key={m.id || i} className={`flex ${own ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-1.5 text-sm ${own ? "bg-primary text-white" : "bg-white border border-border"}`}>
                    {!own && <div className="text-[11px] font-semibold text-primary mb-0.5">{m.sender_name || m.author || m.user_name || "—"}</div>}
                    <div className="whitespace-pre-wrap break-words">{m.text || ((m.attachment || m.file_url) ? "📎 Archivo adjunto" : "")}</div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="flex items-center gap-2 p-2 border-t border-border">
            <input data-testid="chat-widget-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe un mensaje…"
              className="flex-1 h-10 px-3 rounded-sm border border-input text-sm outline-none focus:ring-2 focus:ring-primary" />
            <button type="submit" data-testid="chat-widget-send" className="w-10 h-10 rounded-sm bg-primary text-white flex items-center justify-center hover:bg-orange-600 transition-colors"><PaperPlaneTilt size={18} weight="fill" /></button>
          </form>
        </div>
      )}
    </div>
  );
}
